import { z } from 'zod';
import { DomainError } from '../errors.js';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import { type RaMatrixDefinition, type RiskRating, ratingIn } from './matrix.js';

/**
 * Risk ana verisi (K2; ADR 0086; ortağın FD §3, SS §2, kütüphane Excel'i 01–08). Şirket verisidir:
 * ofis yazar, bütün gemilere iner, gemide salt okunurdur. Kod doğal anahtardır (içe aktarma
 * kodla eşleşir) ve değişmez. Kayıt silinmez, pasiflenir: durum Draft / Active / Retired
 * (FD §3.1). Gemi tarafı yalnız Active kayıtları öneri olarak kullanır. Modül saftır.
 */

/** Gemi tipi grupları (XL 08 Vessel Types; README R15). `ALL` her gemiye uygulanır. */
export const RA_VESSEL_GROUPS = ['ALL', 'CARGO', 'TANKER', 'YACHT_PASSENGER', 'OFFSHORE'] as const;
export type RaVesselGroup = (typeof RA_VESSEL_GROUPS)[number];

/** Geminin grubu (`vessels.ra_group`): `ALL` dışındakiler; boşsa gemiye yalnız `ALL` uygulanır. */
export const RA_SHIP_GROUPS = ['CARGO', 'TANKER', 'YACHT_PASSENGER', 'OFFSHORE'] as const;
export type RaShipGroup = (typeof RA_SHIP_GROUPS)[number];

/** Ana veri durumu (FD §3.1): taslak, etkin, pasif. Kullanılmayan kayıt silinmez, pasiflenir. */
export const RA_MASTER_STATUSES = ['draft', 'active', 'retired'] as const;
export type RaMasterStatus = (typeof RA_MASTER_STATUSES)[number];

/** Tehlike kategorileri (FD §3.2). Excel'de yok; boş gelir, şirket seçer. */
export const RA_HAZARD_CATEGORIES = [
  'mechanical',
  'energy',
  'electrical',
  'chemical',
  'environmental',
  'human',
  'ergonomic',
  'navigation',
  'fire',
  'other',
] as const;
export type RaHazardCategory = (typeof RA_HAZARD_CATEGORIES)[number];

/** Kontrol hiyerarşisi, güçlüden zayıfa (FD §3.3): yok etme, ikame, mühendislik, idari, KKD. */
export const RA_CONTROL_HIERARCHY = [
  'elimination',
  'substitution',
  'engineering',
  'administrative',
  'ppe',
] as const;
export type RaControlHierarchy = (typeof RA_CONTROL_HIERARCHY)[number];

/** Kontrolün aşaması: mevcut (baseline) ya da ek (improvement) önlem (XL 04; FD §5 Tab 4–5). */
export const RA_CONTROL_STAGES = ['existing', 'additional'] as const;
export type RaControlStage = (typeof RA_CONTROL_STAGES)[number];

/** Önleyici ya da hafifletici (FD §3.3); Excel'de yok, boş gelir. */
export const RA_CONTROL_NATURES = ['preventive', 'mitigative'] as const;
/** Zorunlu ya da koşullu (FD §3.3); Excel'de yok, boş gelir. */
export const RA_CONTROL_REQUIREMENTS = ['mandatory', 'conditional'] as const;

/**
 * Satırdaki kontrollerin durumu (XL 05 veri doğrulaması). "Not verified" uygulandı sayılmaz
 * (XL W-01).
 */
export const RA_ROW_CONTROL_STATUSES = [
  'not_verified',
  'implemented',
  'not_implemented',
  'na_reviewed',
] as const;
export type RaRowControlStatus = (typeof RA_ROW_CONTROL_STATUSES)[number];

/** RA türleri (FD §5 Tab 1): rutin, işe özel, dinamik, değişiklikle ilgili. */
export const RA_ASSESSMENT_TYPES = [
  'routine',
  'task_specific',
  'dynamic',
  'change_related',
] as const;
export type RaAssessmentType = (typeof RA_ASSESSMENT_TYPES)[number];

/** Ana veri türleri, ekranın sekme sırasıyla. `links`: faaliyet–tehlike bağı (risk satırı). */
export const RA_MASTER_KINDS = [
  'categories',
  'activities',
  'hazards',
  'consequences',
  'controls',
  'links',
] as const;
export type RaMasterKind = (typeof RA_MASTER_KINDS)[number];

/** Türün tablosu. */
export const RA_MASTER_TABLES = {
  categories: 'ra_categories',
  activities: 'ra_activities',
  hazards: 'ra_hazards',
  consequences: 'ra_consequences',
  controls: 'ra_controls',
  links: 'ra_activity_hazards',
} as const satisfies Record<RaMasterKind, string>;

export const RA_CODE_MAX = 40;
export const RA_MAX_STEPS = 50;
export const RA_MAX_REFS = 50;

/** Kod: büyük harf, rakam, `-`, `_`, `.`; harf ya da rakamla başlar. */
const CODE = /^[A-Z0-9][A-Z0-9._-]{0,39}$/;
const STEP_ID = /^[A-Za-z0-9-]{1,40}$/;

/** Kodun doğal anahtarı: kenar boşluksuz, büyük harf. */
export function raMasterKey(code: string): string {
  return code.trim().toUpperCase();
}

const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');

const text = (max: number) =>
  z
    .string({ error: 'REQUIRED' })
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));

/** Boş metin `null` olur. */
const optionalText = (max: number) =>
  z
    .string({ error: 'INVALID' })
    .transform((v) => v.normalize('NFC').trim())
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((v) => (v === '' ? null : v))
    .nullish()
    .transform((v) => v ?? null);

const code = z
  .string({ error: 'REQUIRED' })
  .transform((v) => v.trim())
  .pipe(z.string().min(1, 'REQUIRED').regex(CODE, 'INVALID_CODE'));
const uuid = z.uuid({ error: 'INVALID' });
const status = z.enum(RA_MASTER_STATUSES, { error: 'INVALID' });
const scale = z
  .number({ error: 'REQUIRED' })
  .int('INVALID')
  .min(1, 'OUT_OF_RANGE')
  .max(5, 'OUT_OF_RANGE');

function uniqueList<T extends z.ZodType>(item: T, min: number, max: number) {
  return z
    .array(item)
    .min(min, 'TOO_FEW')
    .max(max, 'TOO_MANY')
    .refine((list) => new Set(list).size === list.length, 'DUPLICATE');
}

const vesselGroups = uniqueList(z.enum(RA_VESSEL_GROUPS, { error: 'INVALID' }), 1, 5).refine(
  (list) => !list.includes('ALL') || list.length === 1,
  'GROUPS_ALL_EXCLUSIVE',
);

const step = z.strictObject({
  id: z.string({ error: 'REQUIRED' }).regex(STEP_ID, 'INVALID'),
  text: text(300),
});

export const raCategorySchema = z.strictObject({
  code,
  name: text(120),
  vesselGroups,
  status,
});

export const raActivitySchema = z.strictObject({
  code,
  categoryId: uuid,
  name: text(200),
  scope: optionalText(2000),
  steps: z
    .array(step)
    .min(1, 'TOO_FEW')
    .max(RA_MAX_STEPS, 'TOO_MANY')
    .refine((list) => new Set(list.map((s) => s.id)).size === list.length, 'DUPLICATE_ID'),
  vesselGroups,
  assessmentTypes: uniqueList(z.enum(RA_ASSESSMENT_TYPES, { error: 'INVALID' }), 0, 4),
  ownerRole: optionalText(120),
  status,
});

export const raHazardSchema = z.strictObject({
  code,
  /** Tehlike ailesi kodu (XL 03 "Hazard Code", ör. SNAP); 04 ve 05 bununla bağlanır. */
  family: code.nullish().transform((v) => v ?? null),
  name: text(300),
  category: z
    .enum(RA_HAZARD_CATEGORIES, { error: 'INVALID' })
    .nullish()
    .transform((v) => v ?? null),
  mechanism: optionalText(1000),
  consequenceIds: uniqueList(uuid, 0, RA_MAX_REFS),
  exposed: optionalText(300),
  status,
});

export const raConsequenceSchema = z.strictObject({
  code,
  name: text(200),
  status,
});

export const raControlSchema = z
  .strictObject({
    code,
    hazardId: uuid.nullish().transform((v) => v ?? null),
    stage: z.enum(RA_CONTROL_STAGES, { error: 'INVALID' }),
    measure: text(500),
    /**
     * Hiyerarşi etkin kontrolde zorunlu; taslakta boş olabilir (FINAL kütüphane önerilen kontrolü
     * hiyerarşisiz gelir, uydurulmaz; ADR 0098 §4).
     */
    hierarchy: uniqueList(z.enum(RA_CONTROL_HIERARCHY, { error: 'INVALID' }), 0, 5),
    nature: z
      .enum(RA_CONTROL_NATURES, { error: 'INVALID' })
      .nullish()
      .transform((v) => v ?? null),
    requirement: z
      .enum(RA_CONTROL_REQUIREMENTS, { error: 'INVALID' })
      .nullish()
      .transform((v) => v ?? null),
    verificationMethod: optionalText(500),
    evidence: optionalText(500),
    responsibleRole: optionalText(120),
    status,
  })
  .superRefine((v, ctx) => {
    if (v.status !== 'draft' && v.hierarchy.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['hierarchy'], message: 'TOO_FEW' });
    }
  });

/** İlk risk ölçeği: taslak bağda "değerlendirilmedi" (`null`) olabilir (ADR 0098 §4). */
const assessedScale = scale.nullable();

export const raLinkSchema = z
  .strictObject({
    code,
    activityId: uuid,
    stepId: z
      .string({ error: 'INVALID' })
      .regex(STEP_ID, 'INVALID')
      .nullish()
      .transform((v) => v ?? null),
    hazardId: uuid,
    likelihood: assessedScale,
    severity: assessedScale,
    residualLikelihood: scale.nullish().transform((v) => v ?? null),
    residualSeverity: scale.nullish().transform((v) => v ?? null),
    existingControlIds: uniqueList(uuid, 0, RA_MAX_REFS),
    additionalControlIds: uniqueList(uuid, 0, RA_MAX_REFS),
    controlStatus: z.enum(RA_ROW_CONTROL_STATUSES, { error: 'INVALID' }),
    evidence: optionalText(500),
    exposed: optionalText(300),
    status,
  })
  .superRefine((v, ctx) => {
    // İlk risk ya ikisi birlikte ya hiç; etkin (ya da pasif) bağda zorunlu: varsayılan değer
    // verilmez, değerlendirilmemiş öneri yalnız taslakta kalır (FINAL READ ME; ADR 0098 §4)
    if ((v.likelihood === null) !== (v.severity === null)) {
      ctx.addIssue({ code: 'custom', path: ['likelihood'], message: 'INITIAL_INCOMPLETE' });
    } else if (v.likelihood === null && v.status !== 'draft') {
      ctx.addIssue({ code: 'custom', path: ['likelihood'], message: 'NOT_ASSESSED' });
    }
  });

export type RaCategoryData = z.output<typeof raCategorySchema>;
export type RaActivityData = z.output<typeof raActivitySchema>;
export type RaHazardData = z.output<typeof raHazardSchema>;
export type RaConsequenceData = z.output<typeof raConsequenceSchema>;
export type RaControlData = z.output<typeof raControlSchema>;
export type RaLinkData = z.output<typeof raLinkSchema>;

export interface RaMasterData {
  categories: RaCategoryData;
  activities: RaActivityData;
  hazards: RaHazardData;
  consequences: RaConsequenceData;
  controls: RaControlData;
  links: RaLinkData;
}

const SCHEMAS: { [K in RaMasterKind]: z.ZodType<RaMasterData[K]> } = {
  categories: raCategorySchema,
  activities: raActivitySchema,
  hazards: raHazardSchema,
  consequences: raConsequenceSchema,
  controls: raControlSchema,
  links: raLinkSchema,
};

function toIssues(error: z.ZodError): SchemaIssue[] {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join('.'),
    code: issue.message,
  }));
}

/** Biçim denetimi; hata iletileri koddur (`ra.master.errors.<KOD>`). */
export function parseRaMaster<K extends RaMasterKind>(
  kind: K,
  input: unknown,
): ParseResult<RaMasterData[K]> {
  const result = SCHEMAS[kind].safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/** Biçim hatası `INVALID_RA_MASTER` (ayrıntı alan:kod listesi). */
export function raMasterOrThrow<K extends RaMasterKind>(kind: K, input: unknown): RaMasterData[K] {
  const parsed = parseRaMaster(kind, input);
  if (!parsed.ok) {
    throw new DomainError(
      'INVALID_RA_MASTER',
      parsed.issues.map((i) => `${i.path}:${i.code}`).join(','),
      parsed.issues[0]?.path,
    );
  }
  return parsed.value;
}

/** İçe aktarılan faaliyetin adımı: Excel'de tek adım (Task / Work Scope; README §3.1). */
export function raActivitySteps(scope: string): RaActivityData['steps'] {
  return [{ id: 'S1', text: collapse(scope) }];
}

export type RaLinkIssueCode =
  | 'RESIDUAL_WITHOUT_INITIAL'
  | 'RESIDUAL_INCOMPLETE'
  | 'RESIDUAL_ABOVE_INITIAL'
  | 'RESIDUAL_NOT_VERIFIED'
  | 'CONTROL_LISTED_TWICE';

/**
 * Faaliyet–tehlike bağının kural sorunları (XL W-01, W-02; ADR 0050 §3):
 * - Kalan risk bilerek boş olabilir (önlemler doğrulanana dek); doluysa olasılık ve şiddet birlikte.
 * - Kalan risk ilk riskten büyük olamaz.
 * - Kontroller doğrulanmamışken ("Not verified") kalan risk girilmez: tahmin doğrulanmış gibi
 *   görünmez.
 * - Bir kontrol hem mevcut hem ek önlem olamaz.
 */
export function raLinkIssues(link: RaLinkData): RaLinkIssueCode[] {
  const issues: RaLinkIssueCode[] = [];
  const { residualLikelihood: rl, residualSeverity: rs } = link;
  if ((rl === null) !== (rs === null)) issues.push('RESIDUAL_INCOMPLETE');
  else if (rl !== null && rs !== null) {
    if (link.likelihood === null || link.severity === null) {
      issues.push('RESIDUAL_WITHOUT_INITIAL');
    } else if (link.controlStatus === 'not_verified') issues.push('RESIDUAL_NOT_VERIFIED');
    else if (rl * rs > link.likelihood * link.severity) issues.push('RESIDUAL_ABOVE_INITIAL');
  }
  const existing = new Set(link.existingControlIds);
  if (link.additionalControlIds.some((id) => existing.has(id))) {
    issues.push('CONTROL_LISTED_TWICE');
  }
  return issues;
}

/**
 * Bağın ilk ve (varsa) kalan riski, verilen matrisle (16 puan 4 bantta High; ADR 0085).
 * Değerlendirilmemiş taslak bağda ilk risk `null` ("Not assessed"; ADR 0098 §4).
 */
export function raLinkRatings(
  link: Pick<RaLinkData, 'likelihood' | 'severity' | 'residualLikelihood' | 'residualSeverity'>,
  matrix: RaMatrixDefinition,
): { initial: RiskRating | null; residual: RiskRating | null } {
  return {
    initial:
      link.likelihood !== null && link.severity !== null
        ? ratingIn(matrix, link.likelihood, link.severity)
        : null,
    residual:
      link.residualLikelihood !== null && link.residualSeverity !== null
        ? ratingIn(matrix, link.residualLikelihood, link.residualSeverity)
        : null,
  };
}

/**
 * Teknik inceleme durumu (FINAL 05 Developer Schema; ADR 0098 §5): FINAL kütüphaneden gelen
 * faaliyet taslak olarak girer; kıyı incelemesi onaylamadan gemiye "ön incelenmiş" şablon olarak
 * sunulmaz. `null`: bu kapıya girmeyen eski kayıt (v1 içe aktarma, elle giriş).
 */
export const RA_TECHNICAL_REVIEW_STATUSES = [
  'draft',
  'in_review',
  'approved',
  'rejected',
  'superseded',
] as const;
export type RaTechnicalReviewStatus = (typeof RA_TECHNICAL_REVIEW_STATUSES)[number];

/** Faaliyetin şablonu gemiye sunulabilir mi: kapı dışı (`null`) ya da onaylı (FINAL 04 §12). */
export function raTemplateOffered(status: string | null | undefined): boolean {
  return status === null || status === undefined || status === 'approved';
}

/** Kayıt gemiye uygulanır mı: `ALL` her gemiye; grup yalnız o gruptaki gemiye (README R15). */
export function raAppliesTo(
  groups: readonly string[],
  vesselGroup: string | null | undefined,
): boolean {
  return groups.includes('ALL') || (!!vesselGroup && groups.includes(vesselGroup));
}

/**
 * Tehlike adından kategori önerisi (README §3.3: içe aktarma öneri gösterir, kendiliğinden
 * yazmaz). Genel anahtar sözcüklerle; bulunamazsa `null`.
 */
const CATEGORY_HINTS: ReadonlyArray<readonly [RaHazardCategory, RegExp]> = [
  ['electrical', /electric|arc flash|energi[sz]ation|shock/i],
  ['fire', /fire|explosion|ignition|smoke/i],
  ['chemical', /chemical|toxic|vapou?r|fume|cryogenic|oxygen deficiency|atmosphere/i],
  ['ergonomic', /manual handling|ergonomic|lifting by hand|posture/i],
  ['human', /fatigue|alertness|miscommunication|coordination|human|planning error|information/i],
  ['navigation', /collision|grounding|navigation|position|under-keel|contact with structure/i],
  [
    'environmental',
    /weather|ice|noise|dust|biological|discharge to sea|pollution|motion|visibility/i,
  ],
  ['energy', /stored energy|snap-back|pressure|hot surface|hot fluid|energy release/i],
  [
    'mechanical',
    /moving parts|crush|entangle|winch|windlass|suspended load|lifting gear|dropped|falling|fall|slip|trip|equipment|stability|cargo|anchor/i,
  ],
];

export function suggestRaHazardCategory(name: string): RaHazardCategory | null {
  for (const [category, pattern] of CATEGORY_HINTS) if (pattern.test(name)) return category;
  return null;
}
